import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/utils';
import { SearchIcon } from '../icon';
import { IconIdType } from '@/constants/icon';

const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-button text-sm font-medium ring-offset-background transition-all-300 focus-visible:outline-none disabled:pointer-events-none disabled:bg-disable-2',
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground hover:bg-black',
                active:
                    'bg-primaryWeb text-primary-foreground',
                outline:
                    'border-2 border-primaryWeb text-primaryWeb bg-transparent hover:shadow-shadow-4 disabled:bg-white disabled:text-disable-2 disabled:border-disable-2',
                outlineForeground:
                    'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
                ghost:
                    'hover:bg-accent hover:text-accent-foreground',
                ghostRed:
                    'text-primaryWeb hover:shadow-shadow-4',
                link:
                    'text-primary underline-offset-4 hover:underline',
                submit:
                    'bg-primaryWeb text-primary-foreground hover:shadow-shadow-2',
                cancel:
                    'bg-background-3 text-primaryWeb border border-primaryWeb hover:shadow-shadow-4',
                yellow:
                    'bg-customOrange text-primary-foreground hover:shadow-shadow-2',
                outlineYellow:
                    'border-2 border-customOrange text-customOrange bg-transparent hover:shadow-shadow-yellow disabled:bg-white disabled:text-disable-2 disabled:border-disable-2',
                green:
                    'border-2 border-green-1 bg-green-1 text-white hoverable:hover:hover:bg-white hoverable:hover:hover:text-white disabled:bg-white disabled:text-disable-2 disabled:border-disable-2',
                outlineGreen:
                    'border-2 border-green-1 text-green-1 bg-white hoverable:hover:hover:bg-green-1 hoverable:hover:hover:text-white disabled:bg-white disabled:text-disable-2 disabled:border-disable-2',
                report:
                    'border border-red-5 text-red-5',
                input:
                    'bg-background-input px-3 py-2 border border-border-input !rounded-base text-sm font-normal text-text hover:border-primaryWeb disabled:cursor-not-allowed disabled:opacity-1 disabled:bg-disable disabled:border-disable disabled:pointer-events-auto',
                activeMenu:
                    'bg-[#535354] text-[#fff]',
                ghostMenu:
                    'hover:bg-[#3C3C3C]',
                ghostOutlineGray:
                    'bg-gry-light text-customRed hover:bg-customRed hover:text-white',
                paging:
                    '',
                pagingSelected:
                    '',
                none:
                    '',
                filterDefault:
                    'rounded-[5px] !text-fs-mid14 leading-[1] font-medium bg-[#fff] text-text border border-border-gray hoverable:hover:text-primaryWeb hoverable:hover:border-primaryWeb',
                filterActive:
                    'rounded-[5px] !text-fs-mid14 leading-[1] font-medium bg-[#fff] text-primaryWeb border border-primaryWeb'
            },
            size: {
                default: 'h-12 px-2',
                sm: 'h-9 px-3',
                lg: 'h-11 w-36 px-1',
                xl: 'h-12 w-52 px-1',
                icon: 'h-10 w-10',
                input: 'h-12 w-full',
                filter: 'h-[30px] px-[15px] md:px-[20px] py-[5px]'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(buttonVariants({ variant, size }), className)}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = 'Button';

// #region Button Customer
const buttonCusVariants = cva(
    'inline-flex items-center justify-center whitespace-normal overflow-hidden font-semibold rounded-button ring-offset-background transition-all-300 focus-visible:outline-none disabled:pointer-events-none disabled:bg-disable-2 group',
    {
        variants: {
            variant: {
                default:
                    'bg-primaryWeb text-customWhite hoverable:hover:hover:bg-white hoverable:hover:hover:text-primaryWeb border-2 border-primaryWeb disabled:border-none',
                outline:
                    'bg-customWhite text-primaryWeb border-2 border-primaryWeb hoverable:hover:hover:text-white hoverable:hover:hover:bg-primaryWeb disabled:bg-customWhite disabled:text-disable-2 disabled:border-disable-2',
                outlineYellow:
                    'border-2 border-customOrange text-customOrange bg-transparent hoverable:hover:hover:text-white hoverable:hover:hover:bg-customOrange disabled:bg-white disabled:text-disable-2 disabled:border-disable-2',
                green:
                    'bg-customGreen-green2 text-customWhite border-2 border-customGreen-green2 hoverable:hover:hover:text-customGreen-green2 hoverable:hover:bg-white disabled:border-disable-2',
                orange:
                    'bg-orange-org1 text-customWhite hoverable:hover:text-orange-org1 hoverable:hover:bg-white border-2 border-orange-org1 disabled:border-none',
                orange2:
                    'bg-customOrange text-customWhite hoverable:hover:text-customOrange hoverable:hover:bg-white border-2 border-customOrange disabled:border-none',
                black:
                    'bg-primary text-customWhite hoverable:hover:text-primary hoverable:hover:bg-white border-2 border-primary disabled:border-none',
                slide:
                    'rounded-full bg-customWhite',
                search:
                    'bg-customOrange-org2 text-white border-2 border-customOrange-org2 disabled:border-none hoverable:hover:hover:text-customOrange-org2 hoverable:hover:hover:bg-white',
                orange3:
                    'bg-customOrange-org3 text-customWhite hoverable:hover:text-customOrange-org3 hoverable:hover:bg-white border-2 border-customOrange-org3 disabled:border-none',
                outlineOrange3:
                    'border-2 border-customOrange-org3 text-customOrange-org3 bg-transparent hoverable:hover:hover:text-white hoverable:hover:hover:bg-customOrange-org3 disabled:bg-white disabled:text-disable-2 disabled:border-disable-2',
                none:
                    '',
                green2:
                    'bg-customGreen-green3 text-customWhite border-2 border-customGreen-green3 hoverable:hover:hover:text-customGreen-green3 hoverable:hover:bg-white disabled:bg-white disabled:text-disable-2 disabled:border-disable-2'
            },
            size: {
                default: 'h-12 px-4',
                xl: 'w-[320px] h-[60px] px-1',
                lg: 'w-[266px] h-[60px] px-1',
                md: 'w-[208px] h-[48px] px-1',
                sm: 'w-[144px] h-[44px] px-1',
                xsm: 'w-[108px] h-[34px] px-1',
                icon: 'h-10 w-10',
                fit: 'w-fit h-fit px-1.5',
                slide: 'size-[25px] xsm:size-7.5',
                inModal: 'w-[50%] xsm:w-[116px] sm:w-[144px] h-[44px] px-3 sm:px-1'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

export interface ButtonCusProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonCusVariants> {
    asChild?: boolean
}

const ButtonCus = React.forwardRef<HTMLButtonElement, ButtonCusProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(buttonCusVariants({ variant, size }), className)}
                ref={ref}
                {...props}
            />
        );
    }
);
ButtonCus.displayName = 'ButtonCus';

// #region Button with icon
export interface ButtonCusWithIconProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonCusVariants> {
    asChild?: boolean,
    iconId: IconIdType,
    heightIcon?: number | string,
    widthIcon?: number | string
}

const ButtonCusWithIcon = React.forwardRef<
    HTMLButtonElement,
    ButtonCusWithIconProps
>(
    ({ className, variant, size, asChild = false, children, disabled, iconId, heightIcon = 16, widthIcon = 16, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(
                    buttonCusVariants({ variant, size }),
                    'flex justify-center items-center gap-2.5 leading-[1]',
                    className
                )}
                disabled={disabled}
                ref={ref}
                {...props}
            >
                <svg height={heightIcon} width={widthIcon}>
                    <use href={iconId}></use>
                </svg>
                {children}
            </Comp>
        );
    }
);
ButtonCusWithIcon.displayName = 'ButtonCusWithIcon';
// #endregion Button with icon

// #region Button search
export interface ButtonSearchProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonCusVariants> {
    asChild?: boolean
}

const ButtonSearch = React.forwardRef<HTMLButtonElement, ButtonSearchProps>(
    (
        { className, variant = 'search', size, asChild = false, children, disabled, ...props },
        ref
    ) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={cn(
                    buttonCusVariants({ variant, size }),
                    'flex gap-2 group',
                    className
                )}
                disabled={disabled}
                ref={ref}
                {...props}
            >
                <SearchIcon className='transition-all-300 hoverable:group-hover:fill-customOrange-org2' />
                {children}
            </Comp>
        );
    }
);
ButtonSearch.displayName = 'ButtonSearch';
// #endregion Button search

// #endregion Button Customer

export {
    buttonVariants,
    Button,
    buttonCusVariants,
    ButtonCus,
    ButtonSearch,
    ButtonCusWithIcon
};
